import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import {
  TrophyIcon,
  UserGroupIcon,
  CurrencyDollarIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  EyeIcon,
  PlusIcon,
  MinusIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  DocumentTextIcon,
  CalendarDaysIcon
} from '@heroicons/react/24/outline';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement } from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import { adminAPI, labsAPI } from '../../config/api';
import LoadingScreen from '../../components/common/LoadingScreen';

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement);

const Subscriptions = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [filters, setFilters] = useState({
    status: 'all',
    search: '',
    page: 1,
    limit: 10
  });
  const [showGrantModal, setShowGrantModal] = useState(false);
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [selectedLab, setSelectedLab] = useState(null);
  const [grantForm, setGrantForm] = useState({ labId: '', duration: '' });
  const [revokeForm, setRevokeForm] = useState({ reason: '' });
  const [allLabs, setAllLabs] = useState([]);
  const [filteredLabs, setFilteredLabs] = useState([]);
  const [labSearchTerm, setLabSearchTerm] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchData();
  }, [filters]);

  useEffect(() => {
    // Filter labs based on search term
    if (labSearchTerm.trim()) {
      const filtered = allLabs.filter(lab => 
        lab.labName?.toLowerCase().includes(labSearchTerm.toLowerCase()) ||
        lab.user?.name?.toLowerCase().includes(labSearchTerm.toLowerCase()) ||
        lab.user?.email?.toLowerCase().includes(labSearchTerm.toLowerCase())
      );
      setFilteredLabs(filtered);
    } else {
      setFilteredLabs(allLabs);
    }
  }, [allLabs, labSearchTerm]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [statsResponse, subscriptionsResponse] = await Promise.all([
        adminAPI.getSubscriptionStats(),
        adminAPI.getSubscriptions(filters)
      ]);
      
      // Debug API responses
      console.log('API Responses received successfully');
      
      setStats(statsResponse.data.data || statsResponse.data);
      setSubscriptions(
        subscriptionsResponse.data.data?.subscriptions ||
        subscriptionsResponse.data.subscriptions ||
        []
      );
      setPagination(
        subscriptionsResponse.data.data?.pagination ||
        subscriptionsResponse.data.pagination ||
        { page: 1, pages: 1, total: 0 }
      );
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllLabs = async () => {
    try {
      const params = {
        limit: 100,
        page: 1,
        sortBy: 'createdAt',
        sortOrder: 'desc'
      };
      
      const response = await labsAPI.getAll(params);
      // Debug-safe extraction of labs array regardless of wrapper shape
      const data = response?.data || {};
      const labs = data?.data?.labs || data?.labs || [];
      setAllLabs(Array.isArray(labs) ? labs : []);
    } catch (error) {
      console.error('Error fetching labs:', error);
    }
  };

  const handleGrantPremium = async () => {
    if (!grantForm.labId || !grantForm.duration) return;
    
    try {
      setProcessing(true);
      await adminAPI.grantPremium({
        labId: grantForm.labId,
        duration: parseInt(grantForm.duration)
      });
      
      setShowGrantModal(false);
      setGrantForm({ labId: '', duration: '' });
      fetchData();
      // Show success message
    } catch (error) {
      console.error('Error granting premium:', error);
      // Show error message
    } finally {
      setProcessing(false);
    }
  };

  const handleRevokePremium = async () => {
    if (!selectedLab) return;
    
    try {
      setProcessing(true);
      await adminAPI.revokePremium({
        labId: selectedLab._id,
        reason: revokeForm.reason
      });
      
      setShowRevokeModal(false);
      setSelectedLab(null);
      setRevokeForm({ reason: '' });
      fetchData();
      // Show success message
    } catch (error) {
      console.error('Error revoking premium:', error);
      // Show error message
    } finally {
      setProcessing(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    // Always use Gregorian calendar
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return `${amount?.toLocaleString('ar-SA') || 0} ريال`;
  };

  const getStatusColor = (subscription) => {
    const now = new Date();
    const premiumUntil = subscription.premiumUntil ? new Date(subscription.premiumUntil) : null;
    
    if (subscription.isPremium && premiumUntil && premiumUntil > now) {
      return 'text-green-600 bg-green-50';
    }
    return 'text-red-600 bg-red-50';
  };

  const getStatusText = (subscription) => {
    const now = new Date();
    const premiumUntil = subscription.premiumUntil ? new Date(subscription.premiumUntil) : null;
    
    if (subscription.isPremium && premiumUntil && premiumUntil > now) {
      return t('adminSubscriptions.status.active');
    }
    return t('adminSubscriptions.status.expired');
  };

  if (loading) {
    return <LoadingScreen />;
  }

  // Statistics cards data
  const statisticsCards = [
    {
      title: t('adminSubscriptions.statistics.activeSubscriptions'),
      value: stats?.activeSubscriptions || 0,
      icon: TrophyIcon,
      color: 'from-green-500 to-green-600',
      textColor: 'text-green-600'
    },
    {
      title: t('adminSubscriptions.statistics.totalSubscriptions'),
      value: stats?.totalSubscriptions || 0,
      icon: UserGroupIcon,
      color: 'from-blue-500 to-blue-600',
      textColor: 'text-blue-600'
    },
    {
      title: t('adminSubscriptions.statistics.expiredSubscriptions'),
      value: stats?.expiredSubscriptions || 0,
      icon: ClockIcon,
      color: 'from-red-500 to-red-600',
      textColor: 'text-red-600'
    },
    {
      title: t('adminSubscriptions.statistics.totalRevenue'),
      value: formatCurrency(stats?.totalRevenue),
      icon: CurrencyDollarIcon,
      color: 'from-purple-500 to-purple-600',
      textColor: 'text-purple-600'
    },
    {
      title: t('adminSubscriptions.statistics.expiringSoon'),
      value: stats?.expiringSoon || 0,
      icon: ExclamationTriangleIcon,
      color: 'from-orange-500 to-orange-600',
      textColor: 'text-orange-600'
    }
  ];

  // Chart data
  const chartData = {
    labels: ['Active', 'Expired', 'Expiring Soon'],
    datasets: [{
      data: [
        stats?.activeSubscriptions || 0,
        stats?.expiredSubscriptions || 0,
        stats?.expiringSoon || 0
      ],
      backgroundColor: ['#10B981', '#EF4444', '#F59E0B'],
      borderWidth: 0
    }]
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-brand-blue to-brand-green rounded-3xl p-8 text-white"
      >
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2">{t('adminSubscriptions.title')}</h1>
            <p className="text-white/80">{t('adminSubscriptions.subtitle')}</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => {
                fetchAllLabs();
                setLabSearchTerm('');
                setGrantForm({ labId: '', duration: '', reason: '' });
                setShowGrantModal(true);
              }}
              className="bg-white/20 hover:bg-white/30 px-6 py-3 rounded-xl transition-colors flex items-center gap-2"
            >
              <PlusIcon className="h-5 w-5" />
              {t('adminSubscriptions.actions.grantPremium')}
            </button>
            <button
              onClick={fetchData}
              className="bg-white/20 hover:bg-white/30 px-6 py-3 rounded-xl transition-colors flex items-center gap-2"
            >
              <ArrowPathIcon className="h-5 w-5" />
              {t('adminSubscriptions.actions.refreshData')}
            </button>
          </div>
        </div>
      </motion.div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6">
        {statisticsCards.map((card, index) => (
          <motion.div
            key={card.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden"
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className={`p-3 rounded-xl bg-gradient-to-r ${card.color}`}>
                  <card.icon className="h-6 w-6 text-white" />
                </div>
              </div>
              <h3 className="text-sm font-medium text-gray-600 mb-2">{card.title}</h3>
              <p className={`text-2xl font-bold ${card.textColor}`}>
                {typeof card.value === 'string' ? card.value : card.value.toLocaleString()}
              </p>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Charts and Filters Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Subscription Distribution Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6"
        >
          <h3 className="text-lg font-bold text-gray-900 mb-4">
            {t('adminSubscriptions.chartLabels.subscriptionTypes')}
          </h3>
          <div className="h-48">
            <Doughnut 
              data={chartData} 
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: 'bottom'
                  }
                }
              }}
            />
          </div>
        </motion.div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="lg:col-span-2 bg-white rounded-2xl shadow-lg border border-gray-100 p-6"
        >
          <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <FunnelIcon className="h-5 w-5" />
            {t('adminSubscriptions.filters.applyFilters')}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('adminSubscriptions.table.status')}
              </label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">{t('adminSubscriptions.status.all')}</option>
                <option value="active">{t('adminSubscriptions.status.active')}</option>
                <option value="expired">{t('adminSubscriptions.status.expired')}</option>
              </select>
            </div>

            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('adminSubscriptions.filters.search')}
              </label>
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  placeholder={t('adminSubscriptions.filters.search')}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Per Page */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Results per page
              </label>
              <select
                value={filters.limit}
                onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Subscriptions Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
        className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden"
      >
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-bold text-gray-900">
            {t('adminSubscriptions.title')} ({pagination.total})
          </h3>
        </div>

        {subscriptions.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('adminSubscriptions.table.labName')}
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('adminSubscriptions.table.owner')}
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('adminSubscriptions.table.status')}
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('adminSubscriptions.table.expiryDate')}
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('adminSubscriptions.table.amount')}
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('adminSubscriptions.table.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {subscriptions.map((subscription) => (
                    <tr key={subscription._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-gradient-to-r from-brand-blue to-brand-green flex items-center justify-center">
                              <span className="text-white font-medium text-sm">
                                {subscription.labName?.charAt(0)}
                              </span>
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {subscription.labName}
                            </div>
                            {subscription.latestPayment?.invoiceNumber && (
                              <div className="text-xs text-gray-500">
                                {t('adminSubscriptions.table.invoiceNumber')}: {subscription.latestPayment.invoiceNumber}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{subscription.user?.name}</div>
                        <div className="text-xs text-gray-500">{subscription.user?.email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(subscription)}`}>
                          {getStatusText(subscription)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(subscription.premiumUntil)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {subscription.latestPayment?.amount ? formatCurrency(subscription.latestPayment.amount) : '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          {subscription.isPremium ? (
                            <button
                              onClick={() => {
                                setSelectedLab(subscription);
                                setShowRevokeModal(true);
                              }}
                              className="text-red-600 hover:text-red-900 flex items-center gap-1"
                            >
                              <MinusIcon className="h-4 w-4" />
                              {t('adminSubscriptions.actions.revokePremium')}
                            </button>
                          ) : (
                            <button
                              onClick={() => {
                                setGrantForm(prev => ({ ...prev, labId: subscription._id }));
                                fetchAllLabs();
                                setShowGrantModal(true);
                              }}
                              className="text-green-600 hover:text-green-900 flex items-center gap-1"
                            >
                              <PlusIcon className="h-4 w-4" />
                              {t('adminSubscriptions.actions.grantPremium')}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Showing {((pagination.page - 1) * filters.limit) + 1} to {Math.min(pagination.page * filters.limit, pagination.total)} of {pagination.total} results
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleFilterChange('page', pagination.page - 1)}
                    disabled={pagination.page === 1}
                    className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <span className="px-3 py-1 text-sm">
                    Page {pagination.page} of {pagination.pages}
                  </span>
                  <button
                    onClick={() => handleFilterChange('page', pagination.page + 1)}
                    disabled={pagination.page === pagination.pages}
                    className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="p-12 text-center">
            <TrophyIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              {t('adminSubscriptions.messages.noSubscriptions')}
            </h3>
          </div>
        )}
      </motion.div>

      {/* Grant Premium Modal */}
      <AnimatePresence>
        {showGrantModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            onClick={() => setShowGrantModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl p-6 w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                {t('adminSubscriptions.modals.grantPremium.title')}
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('adminSubscriptions.modals.grantPremium.selectLab')}
                  </label>
                  {/* Search Input */}
                  <div className="relative mb-2">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      value={labSearchTerm}
                      onChange={(e) => setLabSearchTerm(e.target.value)}
                      placeholder={t('adminSubscriptions.modals.grantPremium.searchLab')}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  {/* Lab Suggestions (auto-shown while typing) */}
                  {labSearchTerm && (
                    <div className="mt-1 max-h-48 overflow-auto border border-gray-200 rounded-lg bg-white shadow-sm">
                      {filteredLabs.length > 0 ? (
                        filteredLabs.map((lab) => (
                          <button
                            key={lab._id}
                            type="button"
                            onClick={() => {
                              setGrantForm(prev => ({ ...prev, labId: lab._id }));
                              setLabSearchTerm(`${lab.labName} (${lab.user?.email || ''})`);
                            }}
                            className={`w-full text-right px-3 py-2 hover:bg-gray-50 ${grantForm.labId === lab._id ? 'bg-gray-50' : ''}`}
                          >
                            {lab.labName} <span className="text-gray-500 text-xs">({lab.user?.email})</span>
                          </button>
                        ))
                      ) : (
                        <div className="px-3 py-2 text-sm text-gray-500">لا توجد نتائج للبحث</div>
                      )}
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('adminSubscriptions.modals.grantPremium.duration')}
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="12"
                    value={grantForm.duration}
                    onChange={(e) => setGrantForm(prev => ({ ...prev, duration: e.target.value }))}
                    placeholder={t('adminSubscriptions.modals.grantPremium.durationPlaceholder')}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                
              </div>
              <div className="flex space-x-3 mt-6">
                <button
                  onClick={handleGrantPremium}
                  disabled={!grantForm.labId || !grantForm.duration || processing}
                  className="flex-1 bg-brand-blue text-white py-2 px-4 rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {processing ? 'Processing...' : t('adminSubscriptions.modals.grantPremium.confirm')}
                </button>
                <button
                  onClick={() => setShowGrantModal(false)}
                  className="flex-1 bg-gray-200 text-gray-800 py-2 px-4 rounded-lg hover:bg-gray-300"
                >
                  {t('adminSubscriptions.modals.grantPremium.cancel')}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Revoke Premium Modal */}
      <AnimatePresence>
        {showRevokeModal && selectedLab && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            onClick={() => setShowRevokeModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl p-6 w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                {t('adminSubscriptions.modals.revokePremium.title')}
              </h3>
              <p className="text-gray-600 mb-4">
                {t('adminSubscriptions.modals.revokePremium.confirmMessage')}
              </p>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                <div className="flex">
                  <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400 mt-0.5" />
                  <div className="ml-3">
                    <h4 className="text-sm font-medium text-yellow-800">
                      {selectedLab.labName}
                    </h4>
                    <p className="text-sm text-yellow-700">
                      {selectedLab.user?.email}
                    </p>
                  </div>
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('adminSubscriptions.modals.revokePremium.reason')}
                </label>
                <textarea
                  value={revokeForm.reason}
                  onChange={(e) => setRevokeForm(prev => ({ ...prev, reason: e.target.value }))}
                  placeholder={t('adminSubscriptions.modals.revokePremium.reasonPlaceholder')}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={handleRevokePremium}
                  disabled={processing}
                  className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {processing ? 'Processing...' : t('adminSubscriptions.modals.revokePremium.confirm')}
                </button>
                <button
                  onClick={() => setShowRevokeModal(false)}
                  className="flex-1 bg-gray-200 text-gray-800 py-2 px-4 rounded-lg hover:bg-gray-300"
                >
                  {t('adminSubscriptions.modals.revokePremium.cancel')}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Subscriptions;
